<script setup>
/* global axios */
// Disparador — relatório de uma campanha: ações, métricas, configuração e contatos (filtro/busca/export).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const campaignId = route.params.campaignId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const campaign = ref(null);
const contacts = ref([]);
const loading = ref(true);
const statusFilter = ref('');
const search = ref('');

const STATUS = {
  draft: 'bg-n-alpha-2 text-n-slate-11',
  scheduled: 'bg-n-blue-3 text-n-blue-11',
  running: 'bg-n-teal-3 text-n-teal-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-iris-3 text-n-iris-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};
const cStatus = {
  pending: 'text-n-slate-10',
  sent: 'text-n-teal-11',
  failed: 'text-n-ruby-11',
  skipped: 'text-n-amber-11',
  no_whatsapp: 'text-n-ruby-11',
  cancelled: 'text-n-slate-10',
};
const C_STATUS_OPTIONS = [
  'pending',
  'sent',
  'failed',
  'skipped',
  'no_whatsapp',
];

const fmt = iso => (iso ? new Date(iso).toLocaleString() : '—');
const metrics = computed(() => {
  const c = campaign.value || {};
  return [
    { k: 'total', v: c.total_contacts },
    { k: 'sent', v: c.sent_count },
    { k: 'failed', v: c.failed_count },
    { k: 'delivered', v: c.delivered_count },
    { k: 'read', v: c.read_count },
    { k: 'replies', v: c.reply_count },
    { k: 'clicks', v: c.click_count },
  ];
});
const filteredContacts = computed(() =>
  contacts.value.filter(
    ct =>
      (!statusFilter.value || ct.status === statusFilter.value) &&
      (!search.value ||
        `${ct.phone} ${ct.name || ''}`
          .toLowerCase()
          .includes(search.value.toLowerCase()))
  )
);
const canStart = computed(() =>
  ['draft', 'scheduled', 'paused'].includes(campaign.value?.status)
);
const canPause = computed(() => campaign.value?.status === 'running');
const canCancel = computed(
  () =>
    campaign.value &&
    !['completed', 'cancelled'].includes(campaign.value.status)
);
const canRetry = computed(
  () =>
    campaign.value &&
    campaign.value.failed_count > 0 &&
    !['running'].includes(campaign.value.status)
);

const load = async () => {
  loading.value = true;
  try {
    campaign.value = (
      await axios.get(`${base}/campaigns/${campaignId}`)
    ).data.data;
    contacts.value =
      (await axios.get(`${base}/campaigns/${campaignId}/contacts`)).data.data ||
      [];
  } finally {
    loading.value = false;
  }
};
const act = async action => {
  await axios.post(`${base}/campaigns/${campaignId}/${action}`);
  await load();
};
const exportCsv = () => {
  window.open(`${base}/campaigns/${campaignId}/report_export`, '_blank');
};
const back = () =>
  router.push({ name: 'kanban_campaigns', params: { accountId } });

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header class="flex items-center gap-3 px-6 py-4 border-b border-n-weak">
      <button
        class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
        @click="back"
      >
        <svg
          class="w-5 h-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <h1 class="text-heading-1 text-n-slate-12">
        {{ campaign?.name || t('DISPARADOR.loading') }}
      </h1>
      <span
        v-if="campaign"
        class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
        :class="STATUS[campaign.status]"
        >{{ t(`DISPARADOR.status.${campaign.status}`) }}</span
      >
      <div v-if="campaign" class="flex items-center gap-1.5 ml-auto">
        <button
          v-if="canStart"
          class="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
          @click="act('start')"
        >
          {{ t('DISPARADOR.act.start') }}
        </button>
        <button
          v-if="canPause"
          class="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak text-n-amber-11 hover:bg-n-alpha-1"
          @click="act('pause')"
        >
          {{ t('DISPARADOR.act.pause') }}
        </button>
        <button
          v-if="canRetry"
          class="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
          @click="act('retry_failed')"
        >
          {{ t('DISPARADOR.act.retry') }}
        </button>
        <button
          v-if="canCancel"
          class="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-ruby-11 hover:bg-n-ruby-3"
          @click="act('cancel')"
        >
          {{ t('DISPARADOR.act.cancel') }}
        </button>
        <button
          class="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1"
          @click="exportCsv"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
            />
          </svg>
          {{ t('DISPARADOR.act.export') }}
        </button>
      </div>
    </header>

    <div v-if="loading" class="p-6 text-sm text-n-slate-10">
      {{ t('DISPARADOR.loading') }}
    </div>
    <div v-else-if="campaign" class="flex-1 p-6 overflow-y-auto">
      <!-- métricas -->
      <div class="grid grid-cols-2 gap-3 mb-6 md:grid-cols-4 lg:grid-cols-7">
        <div
          v-for="m in metrics"
          :key="m.k"
          class="p-3 rounded-lg border border-n-weak"
        >
          <div class="text-xl font-semibold text-n-slate-12">
            {{ m.v ?? 0 }}
          </div>
          <div class="text-[0.6875rem] text-n-slate-10">
            {{ t(`DISPARADOR.metric.${m.k}`) }}
          </div>
        </div>
      </div>

      <!-- configuração -->
      <h2 class="text-heading-2 mb-2 text-n-slate-12">
        {{ t('DISPARADOR.configTitle') }}
      </h2>
      <div
        class="grid grid-cols-2 gap-x-6 gap-y-1.5 p-4 mb-6 text-sm rounded-lg border border-n-weak md:grid-cols-3"
      >
        <div class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{ t('DISPARADOR.col.source') }}</span
          ><span class="text-n-slate-12">{{
            t(`DISPARADOR.wizard.src.${campaign.source_type}`)
          }}</span>
        </div>
        <div class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{
            t('DISPARADOR.wizard.rotation')
          }}</span
          ><span class="text-n-slate-12">{{
            t(`DISPARADOR.wizard.rot.${campaign.rotation_mode}`)
          }}</span>
        </div>
        <div class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{ t('DISPARADOR.cfg.delay') }}</span
          ><span class="text-n-slate-12">{{
            `${campaign.delay_min_seconds}–${campaign.delay_max_seconds}s`
          }}</span>
        </div>
        <div v-if="campaign.window_start" class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{ t('DISPARADOR.cfg.window') }}</span
          ><span class="text-n-slate-12">{{
            `${campaign.window_start}–${campaign.window_end}`
          }}</span>
        </div>
        <div class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{ t('DISPARADOR.cfg.spintax') }}</span
          ><span class="text-n-slate-12">{{
            campaign.enable_spintax
              ? t('DISPARADOR.cfg.yes')
              : t('DISPARADOR.cfg.no')
          }}</span>
        </div>
        <div class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{ t('DISPARADOR.cfg.followUp') }}</span
          ><span class="text-n-slate-12">{{
            campaign.follow_up_enabled
              ? t('DISPARADOR.cfg.yes')
              : t('DISPARADOR.cfg.no')
          }}</span>
        </div>
        <div v-if="campaign.is_recurring" class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{
            t('DISPARADOR.cfg.recurring')
          }}</span
          ><span class="text-n-slate-12">{{
            t('DISPARADOR.cfg.everyDays', {
              n: campaign.recurring_interval_days,
            })
          }}</span>
        </div>
        <div v-if="campaign.scheduled_at" class="flex justify-between gap-2">
          <span class="text-n-slate-10">{{
            t('DISPARADOR.cfg.scheduledAt')
          }}</span
          ><span class="text-n-slate-12">{{ fmt(campaign.scheduled_at) }}</span>
        </div>
      </div>

      <!-- contatos -->
      <div class="flex items-center gap-2 mb-2">
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('DISPARADOR.contactsTitle') }}
        </h2>
        <input
          v-model="search"
          class="px-2.5 py-1 ml-auto text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :placeholder="t('DISPARADOR.searchContact')"
        />
        <select
          v-model="statusFilter"
          class="pl-2 pr-8.5 py-1 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-11"
        >
          <option value="">{{ t('DISPARADOR.allStatuses') }}</option>
          <option v-for="s in C_STATUS_OPTIONS" :key="s" :value="s">
            {{ t(`DISPARADOR.contactStatus.${s}`) }}
          </option>
        </select>
      </div>
      <p
        v-if="!filteredContacts.length"
        class="py-6 text-sm text-center text-n-slate-10"
      >
        {{ t('DISPARADOR.noContacts') }}
      </p>
      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-10 border-b border-n-weak">
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.phone') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.name') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.status') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.sentAt') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.deliveredAt') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.readAt') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('DISPARADOR.col.error') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ct in filteredContacts"
            :key="ct.id"
            class="border-b border-n-weak"
          >
            <td class="px-3 py-2 text-n-slate-12">{{ ct.phone }}</td>
            <td class="px-3 py-2 text-n-slate-11">{{ ct.name || '—' }}</td>
            <td class="px-3 py-2">
              <span :class="cStatus[ct.status] || 'text-n-slate-10'">{{
                t(`DISPARADOR.contactStatus.${ct.status}`)
              }}</span>
            </td>
            <td class="px-3 py-2 text-n-slate-10">{{ fmt(ct.sent_at) }}</td>
            <td class="px-3 py-2 text-n-slate-10">
              {{ fmt(ct.delivered_at) }}
            </td>
            <td class="px-3 py-2 text-n-slate-10">{{ fmt(ct.read_at) }}</td>
            <td class="px-3 py-2 text-n-ruby-11">
              {{ ct.error_message || '' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
